import { getSidebarItem } from "../../../config/sidebarConfig";
import { circlesQuery, teamMembersQuery } from "../../../services/queries";
import { viewQuery } from "../scopedQuery";
import { toTeam } from "../../team/teamHandover";
import { addableRoles, roleIn, TEAM_ROLES, type TeamRole } from "../../team/teamRoles";
import { askFor } from "./findByName";
import { openPageEntry } from "./openPage";
import { askForNamedCircle, resolveCircle } from "./pickCircle";
import { pickNamed } from "./pickUser";
import { AnswerContext, defineEntry, HandoffEntry, isSlotQuestion } from "./types";
import { aRole, capitalize, countOf, isAre, listOf, listUpTo } from "./wording";

const teamPage = getSidebarItem("team");
const openTeam = (search?: string) => {
  const { pathname, state } = toTeam(search ? { search } : {});
  return { label: `Open ${teamPage.label}`, href: pathname, state };
};

/** The Team, as the Team screen lists it. */
const loadTeam = ({ user, token, queryClient }: AnswerContext) =>
  queryClient.fetchQuery(viewQuery(user, teamMembersQuery(user?.schoolId, user?.soid, token)));

type TeamMember = Awaited<ReturnType<typeof loadTeam>>[number];

/** Someone's role as Team shows it, so a role named in a question matches it whatever its case. */
const isRole = (member: TeamMember, role: string) => member.accessLevel.toLowerCase() === role.toLowerCase();

const NO_TEAM = "There's no one on the Team yet.";

/** "Add Lebo Dlamini as a Creator": ADD MEMBER, filled in. Nothing is saved until Send invite. */
const addMemberEntry: HandoffEntry = {
  id: "team.addMember",
  sidebarItem: "team",
  examples: [
    "Add a team member",
    "Add a new team member as a Creator",
    "Add to the team",
    "Add as a Creator",
    "Add as an Owner",
    "Invite to the team",
    "Invite a team member",
    "Add a new staff member",
    "Add to the staff",
    "Invite to the staff",
    "Add a teacher to the team",
    "New team member",
    "Make them a Creator, not an Admin",
    "Add Sipho Ndlovu as a Creator",
    "Add Anna Smith to the team as an Owner",
  ],
  handoff: async (said, ctx) => {
    const roles = addableRoles(ctx.user);
    if (roles.length === 0) return { kind: "cannot", text: "You can't add team members." };
    const name = said.member?.trim();
    const wanted = roleIn(said.role);
    if (!name) {
      // "Make them a Creator": the role is kept, and said back, while the name is still missing
      const kept = wanted ? `${capitalize(aRole(wanted))} it is. ` : "";
      return askFor("member", "missing", `${kept}What's the new team member's full name?`, []);
    }

    const role = wanted ? roles.find((r) => r.label === wanted) : roles.length === 1 ? roles[0] : undefined;
    if (!role) {
      const question = `Which role should ${name} have?`;
      const text = wanted ? `You can't add someone as ${aRole(wanted)}. ${question}` : question;
      return askFor("role", "missing", text, roles.map((r) => r.label));
    }

    let circle: { circleId: string; name: string } | undefined;
    if (said.audience) {
      // The Circles ADD MEMBER offers: every Circle but the Organization itself
      const circles = (await ctx.queryClient.fetchQuery(viewQuery(ctx.user, circlesQuery(ctx.user?.schoolId, ctx.token)))).filter(
        (c) => c.circleId !== ctx.user?.schoolId
      );
      const question = `Which Circle should ${name} have access to?`;
      const picked = askForNamedCircle(resolveCircle(circles, said.audience), question, circles.map((c) => c.name));
      if (isSlotQuestion(picked)) return picked;
      circle = picked.circle;
    }

    return {
      kind: "ready",
      text: "Here's what I understood. Open it to check. Nothing is saved until you press Send invite.",
      summary: [
        { label: "Name", value: name },
        { label: "Role", value: role.label },
        ...(circle ? [{ label: "Circle", value: circle.name }] : []),
      ],
      to: toTeam({
        addMember: { displayName: name, accessLevel: role.value, ...(circle ? { circleIds: [circle.circleId] } : {}) },
      }),
    };
  },
};

export const teamEntries = [
  openPageEntry("team", "It's where you manage team members and permissions for your Organization.", [
    "Where do I add a team member?",
    "Where can I change someone's role?",
  ]),

  // Everyone on the Team, or everyone with one role, with roles as the Team screen shows them
  defineEntry<{ role: string | null; members: string[] }>({
    id: "team.members",
    sidebarItem: "team",
    examples: [
      "Who's on the Team?",
      "Who is on our team?",
      "Who are the team members?",
      "List the team",
      "Show me the team members",
      "How many team members do we have?",
      "How many team members?",
      "how many team members",
      "Who are the Admins?",
      "Who are the Owners?",
      "Who are the Creators?",
      "Which team members are Admins?",
      "List the staff on the dashboard",
      "Who has access to the dashboard?",
      "Who is already on the team?",
    ],
    slots: { role: "" },
    answer: {
      load: async ({ role: said }, ctx) => {
        const team = await loadTeam(ctx);
        const role = roleIn(said);
        return {
          role,
          members: role
            ? team.filter((m) => isRole(m, role)).map((m) => m.displayName)
            : team.map((m) => `${m.displayName} (${m.accessLevel})`),
        };
      },
      templates: [
        ({ role, members }) =>
          role
            ? members.length === 0
              ? `There are no ${role}s on the Team.`
              : `The Team has ${countOf(members.length, role)}: ${listUpTo(members)}.`
            : members.length === 0
              ? NO_TEAM
              : `There ${isAre(members.length)} ${countOf(members.length, "team member")}: ${listUpTo(members)}.`,
        ({ role, members }) =>
          role
            ? members.length === 0
              ? `No one on the Team is ${aRole(role)}.`
              : `${countOf(members.length, role)} on the Team: ${listUpTo(members)}.`
            : members.length === 0
              ? NO_TEAM
              : `The Team has ${countOf(members.length, "team member")}: ${listUpTo(members)}.`,
        ({ role, members }) =>
          role
            ? members.length === 0
              ? `The Team has no ${role}s.`
              : `On the Team, ${countOf(members.length, role)}: ${listUpTo(members)}.`
            : members.length === 0
              ? NO_TEAM
              : `${countOf(members.length, "team member")} on the Team: ${listUpTo(members)}.`,
      ],
      link: () => openTeam(),
    },
  }),

  // Someone's role, as the Team screen shows it; "each person" is everyone's
  defineEntry<{ name: string; role: string; everyone?: string }>({
    id: "team.role",
    sidebarItem: "team",
    examples: [
      "What role does this team member have?",
      "What is Jane's role?",
      "What role does Jane have?",
      "What role is Jeremy?",
      "What role is Lebo Dlamini?",
      "What's someone's role on the team?",
      "What's their role?",
      "Is the team member an Admin?",
      "Is this person an Owner or a Creator?",
      "What access does the team member have?",
      "What permissions does the team member have?",
      "Which role is the team member?",
      "What is the team member's access level?",
      "Check someone's role",
      "What role does each person have?",
      "What role does Thabo have?",
      "Is Priya an Admin or a Creator?",
    ],
    slots: { member: "" },
    answer: {
      load: async ({ member: said = "" }, ctx) => {
        const team = await loadTeam(ctx);
        if (/\b(?:each|every(?:one|body)?|all)\b/i.test(said)) {
          const everyone = team.length
            ? `${team.map((m) => `${m.displayName} is ${aRole(m.accessLevel)}`).join(". ")}.`
            : `${NO_TEAM} Roles are Admin, Owner and Creator, and each person's role shows on Team.`;
          return { name: "", role: "", everyone };
        }
        const pick = pickNamed(team, (m) => m.displayName, said, "Whose role would you like to know?", {
          slot: "member",
          kind: "team member",
          notNames: new RegExp(`^(?:Team|${TEAM_ROLES.join("|")})s?$`),
        });
        if (isSlotQuestion(pick)) return pick;
        return { name: pick.person.displayName, role: pick.person.accessLevel };
      },
      templates: [
        ({ name, role, everyone }) => everyone ?? `${name} is ${aRole(role)}.`,
        ({ name, role, everyone }) => everyone ?? `${name} is ${aRole(role)} on the Team.`,
        ({ name, role, everyone }) => everyone ?? `On the Team, ${name} is ${aRole(role)}.`,
      ],
      link: (_, { name }) => openTeam(name),
    },
  }),

  // "Can a Creator add people to the team?": yes or no, from who may open Team and what role they may add
  defineEntry<{ role: TeamRole; canOpenTeam: boolean; addableAs: TeamRole[] }>({
    id: "team.canAddMembers",
    sidebarItem: "team",
    examples: [
      "Can a Creator add people to the team?",
      "Can a Creator add a team member?",
      "Can a Creator invite someone to the team?",
      "Can a Creator use Add Member?",
      "Can an Owner add people to the team?",
      "Can an Owner add a team member?",
      "Can an Owner add an Admin?",
      "Can an Admin add a team member?",
      "Is a Creator able to add someone to the team?",
      "Is an Owner able to add an Admin?",
      "Which roles can add people to the team?",
      "Who's allowed to add team members?",
      "Can Creators add staff?",
      "Can I add them as an Admin?",
      "Can I add team members?",
      "Can that Owner make me an Admin?",
    ],
    slots: { role: "" },
    answer: {
      load: async ({ role: said = "" }, ctx) => {
        // "Can I add team members?" is about the role this person has
        const role = roleIn(said) ?? (/\b(?:i|me)\b/i.test(said) ? roleIn(ctx.user?.accessLevel) : null);
        if (!role) {
          return askFor("role", "missing", "Which role do you mean — Admin, Owner or Creator?", [...TEAM_ROLES]);
        }
        const canOpenTeam = teamPage.teamRoles.includes(role);
        const addableAs = canOpenTeam ? addableRoles({ userType: "team", accessLevel: role }).map((r) => r.label) : [];
        return { role, canOpenTeam, addableAs };
      },
      templates: [
        ({ role, canOpenTeam, addableAs }) =>
          canOpenTeam
            ? `Yes — ${aRole(role)} can add team members, as ${listOf(addableAs.map(aRole))}.`
            : `No — ${aRole(role)} can't add team members; only ${listOf(teamPage.teamRoles.map(aRole))} can.`,
        ({ role, canOpenTeam, addableAs }) =>
          canOpenTeam
            ? `${capitalize(aRole(role))} can add team members on the Team page, as ${listOf(addableAs.map(aRole))}.`
            : `${capitalize(aRole(role))} can't add team members — that's for ${listOf(teamPage.teamRoles.map(aRole))} only.`,
        ({ role, canOpenTeam, addableAs }) =>
          canOpenTeam
            ? `Yes, using Add Member — ${aRole(role)} can add ${listOf(addableAs.map(aRole))}.`
            : `No, ${aRole(role)} doesn't have access to the Team page, so they can't add team members.`,
      ],
      link: () => openTeam(),
    },
  }),

  addMemberEntry,
];
